/**
 * Admin Engagement Dashboard
 * Phase 9.5.2: Hidden admin analytics route
 * Displays mood-content engagement heatmaps and behavioral insights
 * Access restricted to brycegonorth@gmail.com
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import Sidebar from '../components/layout/Sidebar';

const ADMIN_EMAIL = 'brycegonorth@gmail.com';

export default function AdminEngagementDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const { data: result, error: rpcError } = await supabase.rpc('get_behavioral_summary');

        if (rpcError) {
          // Handle missing function gracefully
          if (rpcError.code === '42883' || rpcError.message?.includes('does not exist')) {
            console.warn('⚠️ Behavioral summary function not available (migration may not be applied)');
            setError('Analytics function not available. Please apply the database migration.');
            setData([]);
          } else {
            throw rpcError;
          }
        } else {
          setData(result || []);
        }
      } catch (err) {
        console.error('Error fetching engagement data:', err);
        setError(err.message);
        setData([]);
      } finally {
        setLoading(false);
      }
    }

    if (user?.email === ADMIN_EMAIL) {
      fetchData();
    }
  }, [user]);

  // Access control: Only allow admin user
  if (!user || user.email !== ADMIN_EMAIL) {
    return (
      <div className="flex h-screen bg-[#f9f9f8]">
        <Sidebar isExpanded={isExpanded} onToggle={() => setIsExpanded(!isExpanded)} />
        <main className={`flex-1 overflow-y-auto transition-all duration-500 ease-out ${isExpanded ? 'ml-72' : 'ml-16'}`}>
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <h1 className="text-2xl font-semibold text-neutral-800 mb-2">Access Denied</h1>
              <p className="text-neutral-500">This page is restricted to administrators.</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Prepare data for heatmap visualization
  const heatmapData = React.useMemo(() => {
    if (!data || data.length === 0) return [];

    // Group by mood and content_type
    const moodMap = {};
    
    data.forEach(item => {
      const mood = item.mood || 'Unknown';
      const contentType = item.content_type || 'unknown';
      const engagement = item.avg_engagement || 0;
      const count = item.interaction_count || 0;

      if (!moodMap[mood]) {
        moodMap[mood] = { mood };
      }
      
      moodMap[mood][contentType] = {
        value: engagement,
        count: count,
      };
    });

    return Object.values(moodMap);
  }, [data]);

  const contentTypes = React.useMemo(() => {
    const types = new Set();
    data.forEach(item => {
      if (item.content_type) types.add(item.content_type);
    });
    return Array.from(types).sort();
  }, [data]);

  if (loading) {
    return (
      <div className="flex h-screen bg-[#f9f9f8]">
        <Sidebar isExpanded={isExpanded} onToggle={() => setIsExpanded(!isExpanded)} />
        <main className={`flex-1 overflow-y-auto transition-all duration-500 ease-out ${isExpanded ? 'ml-72' : 'ml-16'}`}>
          <div className="p-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Loading engagement data...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[#f9f9f8]">
      <Sidebar isExpanded={isExpanded} onToggle={() => setIsExpanded(!isExpanded)} />
      <main className={`flex-1 overflow-y-auto transition-all duration-500 ease-out ${isExpanded ? 'ml-72' : 'ml-16'}`}>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="mb-6">
            <h1 className="text-3xl font-semibold text-neutral-800 mb-2">Engagement Analytics</h1>
            <p className="text-sm text-neutral-500">Mood-to-content engagement insights</p>
          </div>

          {error && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800">{error}</p>
            </div>
          )}

          {heatmapData.length === 0 ? (
            <div className="bg-white/70 backdrop-blur-lg rounded-2xl border border-neutral-200/50 shadow-sm p-12 text-center">
              <p className="text-neutral-500">No engagement data available yet.</p>
              <p className="text-sm text-neutral-400 mt-2">
                Data will appear as users interact with mood-weighted recommendations.
              </p>
            </div>
          ) : (
            <>
              {/* Heatmap Table */}
              <div className="bg-white/70 backdrop-blur-lg rounded-2xl border border-neutral-200/50 shadow-sm p-6 mb-6">
                <h2 className="text-xl font-semibold text-neutral-800 mb-4">Mood-Content Engagement Heatmap</h2>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>
                        <th className="text-left p-3 border-b border-neutral-200 font-semibold text-sm text-neutral-700">
                          Mood
                        </th>
                        {contentTypes.map(type => (
                          <th
                            key={type}
                            className="text-center p-3 border-b border-neutral-200 font-semibold text-sm text-neutral-700 min-w-[100px]"
                          >
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {heatmapData.map((row, idx) => (
                        <tr key={row.mood || idx} className="hover:bg-neutral-50 transition-colors">
                          <td className="p-3 border-b border-neutral-100 font-medium text-neutral-800">
                            {row.mood}
                          </td>
                          {contentTypes.map(type => {
                            const cell = row[type];
                            const value = cell?.value || 0;
                            const count = cell?.count || 0;
                            
                            // Color intensity based on engagement value (0-1)
                            const intensity = Math.min(1, value);
                            const bgColor = `rgba(139, 92, 246, ${intensity * 0.3 + 0.1})`; // Purple with opacity
                            const textColor = intensity > 0.5 ? 'text-white' : 'text-neutral-700';
                            
                            return (
                              <td
                                key={type}
                                className={`text-center p-3 border-b border-neutral-100 ${textColor}`}
                                style={{ backgroundColor: bgColor }}
                                title={`Engagement: ${(value * 100).toFixed(1)}%, Interactions: ${count}`}
                              >
                                {value > 0 ? (
                                  <div>
                                    <div className="font-semibold">{(value * 100).toFixed(0)}%</div>
                                    <div className="text-xs opacity-75">{count}</div>
                                  </div>
                                ) : (
                                  <span className="text-neutral-400">—</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white/70 backdrop-blur-lg rounded-2xl border border-neutral-200/50 shadow-sm p-4">
                  <div className="text-sm text-neutral-500 mb-1">Total Interactions</div>
                  <div className="text-2xl font-semibold text-neutral-800">
                    {data.reduce((sum, item) => sum + (item.interaction_count || 0), 0)}
                  </div>
                </div>
                <div className="bg-white/70 backdrop-blur-lg rounded-2xl border border-neutral-200/50 shadow-sm p-4">
                  <div className="text-sm text-neutral-500 mb-1">Mood Combinations</div>
                  <div className="text-2xl font-semibold text-neutral-800">{heatmapData.length}</div>
                </div>
                <div className="bg-white/70 backdrop-blur-lg rounded-2xl border border-neutral-200/50 shadow-sm p-4">
                  <div className="text-sm text-neutral-500 mb-1">Content Types</div>
                  <div className="text-2xl font-semibold text-neutral-800">{contentTypes.length}</div>
                </div>
              </div>

              {/* Raw Data Table (for debugging) */}
              {import.meta.env.DEV && (
                <div className="bg-white/70 backdrop-blur-lg rounded-2xl border border-neutral-200/50 shadow-sm p-6">
                  <h2 className="text-xl font-semibold text-neutral-800 mb-4">Raw Data (Dev Only)</h2>
                  <div className="overflow-x-auto">
                    <pre className="text-xs bg-neutral-50 p-4 rounded-lg overflow-auto max-h-96">
                      {JSON.stringify(data, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

